import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  KeyIcon,
  NoSymbolIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'
import { parseDate, formatRoleName } from 'utils'
import { showAlert } from 'services/alerts.service'
import TableComponent from 'components/common/TableComponent'
import ActionCardComponent from 'components/common/ActionCardComponent'
import ButtonComponent from 'components/common/ButtonComponent'
import ModalComponent from 'components/common/ModalComponent'
import PageLoadingState from 'components/common/PageLoadingState'
import StatusBadge from 'components/System/StatusBadge'
import TenantFormModal from 'components/System/TenantFormModal'
import TenantUserFormModal from 'components/System/TenantUserFormModal'
import ResetPasswordModal from 'components/System/ResetPasswordModal'
import ConfirmModal from 'components/System/ConfirmModal'
import { systemRequest, getRequestErrorMessage } from 'components/System/systemRequest'

// Per-role user columns. The owner admin is shown in its own column; usersCount
// still includes every user of the farm.
const TENANT_ROLES = ['personnelManager', 'operationsManager', 'agronomist']

const fullName = (firstName, lastName) => [firstName, lastName].filter(Boolean).join(' ')

const ActionButton = ({ onClick, title, children }) => (
  <button
    type='button'
    onClick={onClick}
    title={title}
    aria-label={title}
    className='p-1 rounded transition hover:bg-gray-100'
  >
    {children}
  </button>
)

const Tenants = () => {
  const { t } = useTranslation()
  const [state, setState] = useState({
    loading: true,
    tenants: [],
    latestAppVersion: null,
    search: '',
    selectedTenant: null,
    loadingTenant: false,
    roles: [],
    permissionsList: []
  })
  // { tenant } (null tenant = create)
  const [tenantForm, setTenantForm] = useState(null)
  // { user } (null user = create)
  const [userForm, setUserForm] = useState(null)
  const [passwordUser, setPasswordUser] = useState(null)
  // { title, message, confirmLabel, confirmText, danger, run }
  const [confirm, setConfirm] = useState(null)

  const getTenants = async () => {
    try {
      const response = await systemRequest('GET', '/system/tenants')
      if (response.status === 200) {
        setState(s => ({
          ...s,
          tenants: response.data.tenants || [],
          latestAppVersion: response.data.latestAppVersion
        }))
      }
    } catch (error) {
      console.error('Error fetching tenants:', error)
      showAlert({ message: t('system.tenants.fetchError'), icon: 'error' })
    } finally {
      setState(s => ({ ...s, loading: false }))
    }
  }

  // Roles and permissions for the user form
  const getUserFormOptions = async () => {
    try {
      const [roles, permissions] = await Promise.all([
        systemRequest('GET', '/management/permissions/roles'),
        systemRequest('GET', '/management/permissions')
      ])
      setState(s => ({
        ...s,
        roles: Array.isArray(roles.data) ? roles.data : [],
        permissionsList: Array.isArray(permissions.data) ? permissions.data : []
      }))
    } catch (error) {
      console.error('Error fetching roles and permissions:', error)
    }
  }

  useEffect(() => {
    getTenants()
    getUserFormOptions()
  }, [])

  const loadTenant = async farmId => {
    try {
      const response = await systemRequest('GET', `/system/tenants/${farmId}`)
      if (response.status === 200) {
        setState(s => ({ ...s, selectedTenant: response.data.tenant }))
      }
    } catch (error) {
      console.error('Error fetching tenant:', error)
      showAlert({ message: t('system.tenants.fetchTenantError'), icon: 'error' })
    } finally {
      setState(s => ({ ...s, loadingTenant: false }))
    }
  }

  const openTenant = tenant => {
    setState(s => ({ ...s, loadingTenant: true, selectedTenant: { ...tenant, users: [], devices: [] } }))
    loadTenant(tenant.farmId)
  }

  const closeTenant = () => setState(s => ({ ...s, selectedTenant: null }))

  // After any change: refresh the list and, if open, the tenant detail
  const refresh = () => {
    getTenants()
    if (state.selectedTenant) loadTenant(state.selectedTenant.farmId)
  }

  const runConfirmed = async (request, successMessage, afterSuccess = refresh) => {
    try {
      await request()
      showAlert({ message: successMessage, icon: 'success' })
      setConfirm(null)
      afterSuccess()
    } catch (error) {
      showAlert({ message: getRequestErrorMessage(error, t('system.tenants.actionError')), icon: 'error' })
    }
  }

  const askTenantStatus = tenant => {
    const suspend = tenant.isActive
    setConfirm({
      title: t(suspend ? 'system.tenants.confirm.suspendTitle' : 'system.tenants.confirm.reactivateTitle'),
      message: t(suspend ? 'system.tenants.confirm.suspendMessage' : 'system.tenants.confirm.reactivateMessage', { name: tenant.farmName }),
      confirmLabel: t(suspend ? 'system.tenants.actions.suspend' : 'system.tenants.actions.reactivate'),
      danger: suspend,
      run: () => runConfirmed(
        () => systemRequest('PATCH', `/system/tenants/${tenant.farmId}/status`, { isActive: !suspend }),
        t(suspend ? 'system.tenants.confirm.suspended' : 'system.tenants.confirm.reactivated', { name: tenant.farmName })
      )
    })
  }

  const askTenantDelete = tenant => {
    setConfirm({
      title: t('system.tenants.confirm.deleteTitle'),
      message: t('system.tenants.confirm.deleteMessage', { name: tenant.farmName }),
      confirmLabel: t('common.delete'),
      confirmText: tenant.farmName,
      danger: true,
      run: () => runConfirmed(
        () => systemRequest('DELETE', `/system/tenants/${tenant.farmId}`),
        t('system.tenants.confirm.deleted', { name: tenant.farmName }),
        () => {
          closeTenant()
          getTenants()
        }
      )
    })
  }

  const askUserStatus = user => {
    const { farmId } = state.selectedTenant
    const deactivate = user.isActive
    setConfirm({
      title: t(deactivate ? 'system.tenants.confirm.deactivateUserTitle' : 'system.tenants.confirm.activateUserTitle'),
      message: t(deactivate ? 'system.tenants.confirm.deactivateUserMessage' : 'system.tenants.confirm.activateUserMessage', { email: user.email }),
      confirmLabel: t(deactivate ? 'system.tenants.actions.deactivate' : 'system.tenants.actions.activate'),
      danger: deactivate,
      run: () => runConfirmed(
        () => systemRequest('PATCH', `/system/tenants/${farmId}/users/${user.userId}/status`, { isActive: !deactivate }),
        t(deactivate ? 'system.tenants.confirm.userDeactivated' : 'system.tenants.confirm.userActivated', { email: user.email })
      )
    })
  }

  const askUserDelete = user => {
    const { farmId } = state.selectedTenant
    setConfirm({
      title: t('system.tenants.confirm.deleteUserTitle'),
      message: t('system.tenants.confirm.deleteUserMessage', { email: user.email }),
      confirmLabel: t('common.delete'),
      danger: true,
      run: () => runConfirmed(
        () => systemRequest('DELETE', `/system/tenants/${farmId}/users/${user.userId}`),
        t('system.tenants.confirm.userDeleted', { email: user.email })
      )
    })
  }

  if (state.loading) {
    return <PageLoadingState message={t('system.tenants.loading')} rows={6} />
  }

  const { latestAppVersion, selectedTenant } = state
  const search = state.search.toLowerCase()
  const statusLabel = active => t(active ? 'system.tenants.status.active' : 'system.tenants.status.suspended')
  const userStatusLabel = active => t(active ? 'system.tenants.status.active' : 'system.tenants.status.inactive')

  const rows = state.tenants
    .map(tenant => ({
      ...tenant,
      adminName: fullName(tenant.adminFirstName, tenant.adminLastName),
      status: statusLabel(tenant.isActive),
      ...Object.fromEntries(TENANT_ROLES.map(role => [role, tenant.usersByRole?.[role] || 0]))
    }))
    .filter(tenant => !search ||
      [tenant.farmName, tenant.adminName, tenant.adminEmail].some(value => String(value || '').toLowerCase().includes(search)))

  const tenantsTableData = {
    columns: [
      { key: 'farmName', labelKey: 'system.tenants.cols.farm' },
      {
        key: 'status',
        labelKey: 'system.tenants.cols.status',
        cell: (value, row) => <StatusBadge active={row.isActive} label={value} />
      },
      {
        key: 'adminName',
        labelKey: 'system.tenants.cols.admin',
        cell: (value, row) => (
          <div className='flex flex-col'>
            <span>{value || '-'}</span>
            <span className='text-xs text-gray-500'>{row.adminEmail}</span>
          </div>
        )
      },
      ...TENANT_ROLES.map(role => ({ key: role, label: formatRoleName(role) })),
      { key: 'usersCount', labelKey: 'system.tenants.cols.users' },
      {
        key: 'devicesCount',
        labelKey: 'system.tenants.cols.devices',
        cell: (value, row) => latestAppVersion
          ? (
            <span className={row.devicesOnLatest < value ? 'text-amber-600 font-medium' : ''}>
              {t('system.tenants.onLatest', { onLatest: row.devicesOnLatest, total: value })}
            </span>
            )
          : value
      },
      { key: 'createdAt', labelKey: 'system.tenants.cols.createdAt', cell: value => parseDate(value, false) }
    ],
    rows
  }

  const usersTableData = {
    columns: [
      {
        key: 'email',
        labelKey: 'system.tenants.cols.email',
        cell: (value, row) => (
          <span>
            {value}
            {row.isOwner && <span className='ml-2 text-xs text-gray-500'>({t('system.tenants.owner')})</span>}
          </span>
        )
      },
      { key: 'firstName', labelKey: 'system.tenants.cols.name', cell: (value, row) => fullName(value, row.lastName) },
      { key: 'roleName', labelKey: 'system.tenants.cols.role', cell: value => formatRoleName(value || '') },
      {
        key: 'status',
        labelKey: 'system.tenants.cols.status',
        cell: (value, row) => <StatusBadge active={row.isActive} label={value} />
      },
      { key: 'createdAt', labelKey: 'system.tenants.cols.createdAt', cell: value => parseDate(value, false) },
      {
        key: 'actions',
        labelKey: 'mgmt.common.actions',
        cell: (value, row) => (
          <div className='flex items-center gap-1'>
            <ActionButton onClick={() => setUserForm({ user: row })} title={t('common.edit')}>
              <PencilSquareIcon className='w-5 h-5 text-blue-500' />
            </ActionButton>
            <ActionButton onClick={() => setPasswordUser(row)} title={t('system.tenants.actions.resetPassword')}>
              <KeyIcon className='w-5 h-5 text-gray-600' />
            </ActionButton>
            {!row.isOwner && (
              <>
                <ActionButton
                  onClick={() => askUserStatus(row)}
                  title={t(row.isActive ? 'system.tenants.actions.deactivate' : 'system.tenants.actions.activate')}
                >
                  {row.isActive
                    ? <NoSymbolIcon className='w-5 h-5 text-amber-600' />
                    : <CheckCircleIcon className='w-5 h-5 text-green-600' />}
                </ActionButton>
                <ActionButton onClick={() => askUserDelete(row)} title={t('common.delete')}>
                  <TrashIcon className='w-5 h-5 text-red-500' />
                </ActionButton>
              </>
            )}
          </div>
        )
      }
    ],
    rows: (selectedTenant?.users || []).map(user => ({ ...user, status: userStatusLabel(user.isActive) }))
  }

  const devicesTableData = {
    columns: [
      { key: 'identifier', labelKey: 'system.tenants.cols.identifier' },
      { key: 'groupName', labelKey: 'system.tenants.cols.group', cell: value => value || '-' },
      {
        key: 'appVersion',
        labelKey: 'system.tenants.cols.appVersion',
        cell: value => (
          <span className={latestAppVersion && value !== latestAppVersion ? 'text-amber-600 font-medium' : ''}>
            {value || '-'}
          </span>
        )
      },
      { key: 'lastSync', labelKey: 'system.tenants.cols.lastSync', cell: value => parseDate(value) }
    ],
    rows: selectedTenant?.devices || []
  }

  return (
    <>
      <ActionCardComponent>
        <div data-align='left' className='flex flex-col'>
          <h3 className='text-lg font-semibold text-greenPrimary'>{t('system.tenants.title')}</h3>
          <span className='text-sm text-gray-500'>
            {t('system.tenants.summary', { count: state.tenants.length })}
            {latestAppVersion ? ` · ${t('system.tenants.latestVersion', { version: latestAppVersion })}` : ''}
          </span>
        </div>
        <input
          type='text'
          placeholder={t('system.tenants.search')}
          value={state.search}
          onChange={e => setState(s => ({ ...s, search: e.target.value }))}
          className='py-2 px-3 border rounded-md shadow-sm'
        />
        <ButtonComponent onClick={() => setTenantForm({ tenant: null })}>
          <PlusIcon className='w-5 h-5 action-icon' />
          {t('system.tenants.actions.newTenant')}
        </ButtonComponent>
      </ActionCardComponent>

      <TableComponent
        tableData={tenantsTableData}
        defaultSortColumn='farmName'
        onRowClick={openTenant}
        exportFileName='tenants'
      />

      <ModalComponent
        isOpen={!!selectedTenant}
        onClose={closeTenant}
        title={selectedTenant?.farmName || ''}
        maxWidth='5xl'
        hideDefaultFooter
      >
        {state.loadingTenant
          ? <PageLoadingState message={t('system.tenants.loading')} rows={4} />
          : (
            <div className='flex flex-col gap-6'>
              <div className='flex flex-col gap-3 md:flex-row md:items-center md:justify-between'>
                <div className='flex flex-wrap items-center gap-2 text-sm text-gray-700'>
                  <StatusBadge active={selectedTenant?.isActive} label={statusLabel(selectedTenant?.isActive)} />
                  <span className='font-semibold'>{t('system.tenants.cols.admin')}:</span>
                  {fullName(selectedTenant?.adminFirstName, selectedTenant?.adminLastName) || '-'}
                  {selectedTenant?.adminEmail ? ` (${selectedTenant.adminEmail})` : ''}
                </div>
                <div className='flex flex-wrap gap-2'>
                  <button
                    type='button'
                    onClick={() => setTenantForm({ tenant: selectedTenant })}
                    className='px-3 py-1.5 rounded bg-gray-100 text-greenPrimary hover:bg-gray-200 text-sm'
                  >
                    {t('common.edit')}
                  </button>
                  <button
                    type='button'
                    onClick={() => askTenantStatus(selectedTenant)}
                    className={`px-3 py-1.5 rounded text-sm ${selectedTenant?.isActive ? 'bg-amber-100 text-amber-800 hover:bg-amber-200' : 'bg-green-100 text-green-800 hover:bg-green-200'}`}
                  >
                    {t(selectedTenant?.isActive ? 'system.tenants.actions.suspend' : 'system.tenants.actions.reactivate')}
                  </button>
                  <button
                    type='button'
                    onClick={() => askTenantDelete(selectedTenant)}
                    className='px-3 py-1.5 rounded bg-red-100 text-red-700 hover:bg-red-200 text-sm'
                  >
                    {t('common.delete')}
                  </button>
                </div>
              </div>
              <div>
                <div className='flex items-center justify-between mb-2'>
                  <h4 className='font-semibold text-greenPrimary'>
                    {t('system.tenants.usersTitle', { count: usersTableData.rows.length })}
                  </h4>
                  <button
                    type='button'
                    onClick={() => setUserForm({ user: null })}
                    className='flex items-center gap-1 px-3 py-1.5 rounded bg-greenPrimary text-white hover:opacity-90 text-sm'
                  >
                    <PlusIcon className='w-4 h-4' />
                    {t('system.tenants.actions.newUser')}
                  </button>
                </div>
                <TableComponent tableData={usersTableData} defaultSortColumn='roleName' exportable={false} />
              </div>
              <div>
                <h4 className='font-semibold text-greenPrimary mb-2'>
                  {t('system.tenants.devicesTitle', { count: devicesTableData.rows.length })}
                </h4>
                <TableComponent tableData={devicesTableData} defaultSortColumn='identifier' exportable={false} />
              </div>
            </div>
            )}
      </ModalComponent>

      {/* Rendered after (not inside) the detail modal: its content is a <form> */}
      <TenantFormModal
        isOpen={!!tenantForm}
        tenant={tenantForm?.tenant || null}
        onClose={() => setTenantForm(null)}
        onSaved={() => {
          setTenantForm(null)
          refresh()
        }}
      />

      <TenantUserFormModal
        isOpen={!!userForm}
        farmId={selectedTenant?.farmId}
        user={userForm?.user || null}
        roles={state.roles}
        permissionsList={state.permissionsList}
        onClose={() => setUserForm(null)}
        onSaved={() => {
          setUserForm(null)
          refresh()
        }}
      />

      <ResetPasswordModal
        isOpen={!!passwordUser}
        farmId={selectedTenant?.farmId}
        user={passwordUser}
        onClose={() => setPasswordUser(null)}
        onSaved={() => setPasswordUser(null)}
      />

      <ConfirmModal
        isOpen={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        confirmText={confirm?.confirmText}
        danger={confirm?.danger}
        onConfirm={() => confirm?.run()}
        onClose={() => setConfirm(null)}
      />
    </>
  )
}

export default Tenants
